import React, { useEffect, useState } from 'react'
import { cn } from '@utils/safeTs'
import styles from './MyAccountRitardiList.module.sass'
import useTrans from '@hooks/useTrans'
import { FornitoreType, TecnicaPadreType } from '@utils/types'
import MyAccountRitardiListRow from '@components/molecules/MyAccountRitardiListRow/MyAccountRitardiListRow'
import { Button, FormCheckbox, FormField, FormInput, FormRitardoInput, FormSelect, MyAccountTable } from '@components/atoms'
import { MyAccountTableHeadersType } from '@components/atoms/MyAccountTable/MyAccountTable'
import FormBlurSaveInput from '@components/atoms/FormBlurSaveInput/FormBlurSaveInput'


type RitardiValuesType = { 
  generic: string,
  parents: { 
    [k: number]: {
      parent: string
      children: { [k: number]: string }
    } 
  }, 
}

interface Props {
  filteredResults?: TecnicaPadreType[]
  fornitore?: FornitoreType
  values?: RitardiValuesType,
  setValues?: any
  className?: string
}

const MyAccountRitardiList = (props: Props) => {

  const { 
    filteredResults = [],
    fornitore,
    values,
    setValues,
    className = '',
  } = props

  const t = useTrans()

  const actionsOptions = [
    { label: 'Personalizza', value: 'customize' },
    { label: 'Rimuovi personalizzazione', value: 'uncustomize' },
    { label: 'Imposta valore personalizzato', value: 'customValue' },
  ]

  const [selectAll, setSelectAll] = useState(false)
  const [selection, setSelection] = useState({ parents: [], children: [] })
  const [selectedAction, setSelectedAction] = useState(undefined)
  const [customValue, setCustomValue] = useState(undefined)

  const headers: MyAccountTableHeadersType = [
    { key: 'check', label: (
      <FormCheckbox 
        id={`myaccountritardilistrow_check_all`}
        checked={selectAll}
        onChange={(e) => {
          setSelection(e.target?.checked ? {
            parents: filteredResults?.map(t => t.id),
            children: filteredResults.reduce((res, t) => res.concat(t.tecniche.map(c => c.id)), [])
          } : {
            parents: [],
            children: [],
          })
          setSelectAll(e.target.checked)
        }}
      />
    ) },
    { key: 'tecnica', label: t("Tecnica") },
    { key: 'ritardo', label: t("Ritardo") },
    { key: 'note' },
    { key: 'action' },
  ]

  useEffect(() => {
    setSelectAll(false)
  }, [filteredResults])

  useEffect(() => {
    console.log(selection, values)
  }, [values])

  const getActionValue = (customizeValue) => {
    switch (selectedAction) {
      case "uncustomize":
        return undefined
      case "customize":
        return customizeValue
      case "customValue":
        return customValue
    }
  } 

  const isCustom = (v) => typeof(v) !== 'undefined'
  const isEditable = (v) => isCustom(v) || selectedAction !== 'customValue'

  const executeAction = () => {
    if ((selectedAction !== 'customValue' || !!parseInt(customValue)) && (selection.parents?.length || selection.children?.length)){
      // TODO - salvataggio bulk 
      // save(...)

      setValues({
        ...values, 
        parents: Object.keys(values.parents).reduce((res, tId) => {

          return {
            ...res, 
            [tId]: {
              ...values.parents[tId],
              ...(isEditable(values.parents[tId]?.parent) && selection.parents.includes(parseInt(tId))) ? { 
                parent: getActionValue(
                  // il parametro customizeValue serve solo per 'customize': se è già custom mantengo lo stesso valore altrimenti eredito il generico
                  isCustom(values.parents[tId].parent) ? values.parents[tId].parent : values.generic
                ) 
              } : {},
              children: {
                ...values.parents[tId]?.children,
                ...Object.keys(values.parents[tId]?.children).reduce((resC, cId) => (
                  isEditable(values.parents[tId]?.children?.[cId]) && selection.children.includes(parseInt(cId)) 
                  ? ({
                    ...resC, 
                    [cId]: getActionValue(
                      // il parametro customizeValue serve solo per 'customize'
                      selectedAction === 'customize' 
                        ? isCustom(values.parents[tId]?.children?.[cId]) 
                          ? values.parents[tId]?.children?.[cId] // se il valore è già personalizzato, non cambia nulla
                          : isCustom(values.parents[tId]?.parent) 
                            ? values.parents[tId].parent // se il parent è già personalizzato prende il suo valore
                            : values.generic // altrimenti il generico
                        : values.parents[tId]?.parent
                    ) 
                  }) : resC), {})
              }
            }
          }
        }, {}),
      })
    }
  }

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.generic}>
        <FormField label={t("Generico")} id={`myaccountritardi_generic_${fornitore?.id}`}>
          <FormRitardoInput
            level="generic"
            objectId={fornitore?.id}
            value={values?.generic}
            setValue={(v) => setValues({...values, generic: v })}
          />
        </FormField>
      </div>
      {!!filteredResults.length ? (
        <div className={styles.list}>
          <MyAccountTable headers={headers} className={styles.table}>
            {filteredResults.map((tecnica) => (
              <MyAccountRitardiListRow
                key={tecnica.id}
                selection={selection}
                setSelection={(v) => {
                  setSelectAll(false)
                  setSelection(v)
                }}
                tecnicaPadre={tecnica}
                values={values ? {
                  ...values.parents?.[tecnica.id],
                  generic: values.generic,
                } : undefined}
                setValues={(v) => {
                  setValues({
                    ...values, 
                    parents: {
                      ...values.parents, 
                      [tecnica.id]: {
                        parent: v.parent,
                        children: v.children,
                      }
                    }
                  })
                }}
              />
            ))}
          </MyAccountTable>
          <div className={styles.listBottom}>
            <div className={styles.actions}>
              <FormField className={styles.actionsSelect}>
                <FormSelect 
                  placeholder={t("Seleziona azione multipla")}
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  options={actionsOptions}
                />
              </FormField>
              {selectedAction === 'customValue' && (
                <FormField className={styles.actionsCustomValueInput}>
                  <FormInput 
                    id={`myaccountritardilist_actionscustomvalue_${fornitore.id}`}
                    type="number"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                  />
                </FormField>
              )}
              <Button
                label={t("Esegui")}
                variant="primary"
                size="medium"
                disabled={!selectedAction || (selectedAction === 'customValue' && !parseInt(customValue)) || (!selection.parents?.length && !selection.children?.length)}
                onClick={executeAction}
              />
            </div>
            {(selection.parents?.length || 0) + (selection.children?.length || 0) > 0 && (
              <span className={styles.info} dangerouslySetInnerHTML={{ __html:
                t("<strong>{0}</strong> elementi selezionati", [((selection.parents?.length || 0) + (selection.children?.length || 0)).toString()])
              }} />
            )}
          </div>
        </div>
      ) : (
        <div className={styles.empty}>
          {t("Nessun risultato")}
        </div>
      )}
    </div>
  )
}

export default MyAccountRitardiList
